<script setup lang="ts">
/**
 * /settlements —— 结算与对账(全新页,v2 · O2;S-43)。
 *
 * ═══ 这一页为什么存在(规格 §6 第一层)═══
 *
 * 结算的两条只读接口从 S-09 起就在契约里,demo 一直直接打 HTTP ——
 * **运营端从来没有结算页面**,要在浏览器里看这笔账做不到:一单完成之后
 * 钱怎么分(应付承运商 / 平台佣金 / 代办费)、尾差落在哪一侧、退款冲正之后
 * 那几个数还守不守恒,运营只能去查库。这一页把那两条接口搬上屏幕。
 *
 * ═══ 这一页只读 ═══
 *
 * 只调契约里的两条 GET(listSettlements / getSettlement),没有任何写请求:
 * 打款走线下对公转账(paid_out 本期没有通道),取消退款走的是小程序那条链路(S-08),
 * 运营端在结算数据上不制造状态。
 *
 * ═══ 三张统计卡为什么画「—」(这一页最容易撒谎的地方)═══
 *
 * 画板那三张卡(待结算 / 本月已结 / 平台佣金)在契约里**没有数据源**:
 *   · 列表接口只有 page / pageSize,返回体是逐条流水 —— 没有按状态的计数,
 *     也没有任何求和;更没有任何时间字段,所以「本月」这个口径今天算不出来;
 *   · paid_out(平台真的打款)本期没有任何路径产出(线下对公转账,Y-19);
 *   · **不许数本页条数**:一页 50 条里的数说成全量,页面上完全看不出来,
 *     演示时说出口就是一个错数字(同 Y-26 / AS-087 在别处的口径)。
 * 所以三张卡画「—」,各自登记一条 @unimplemented 标记(见下面那三条),
 * 口径写在 PageHeader 的说明里。
 *
 * ═══ 与画板的已知差异(都记在 design-copy 的 O2 帧里)═══
 *
 * ① 表格九列照画,其中「承运商」「成交时间」两列在 Settlement 契约里没有字段
 *    (既没有承运商,也没有任何时间字段)—— 画破折号,各登记一条 @unimplemented 标记。
 *    不拿订单接口逐行顶替:那是每页 50 次请求,而且订单上的「下单时间」
 *    与画板要的「成交时间」是两回事。
 * ② 画板样例行里的「应付承运商 = 实付 − 佣金」漏减了代办费,与契约等式
 *    (实付 = 应付承运商 + 佣金 + 代办费)不符 —— 实现按契约算,画板数字是数据不锁。
 * ③ 画板的行标签「已结算」「结算失败」两个词本期不可达(前者是 paid_out,
 *    本期没有产出路径;后者在四个取值里没有对应档,冲正不是失败)——
 *    登记进 _notRequired,页面渲染的是 settlementStatusLabel() 给出的真值。
 * ④ 行入口是**单号那一格**(画板没有行入口也没有操作列):九列一列不多,
 *    点单号打开右侧抽屉看这笔账的九个数。列表按契约分页(每页 50 条)——
 *    画板没画分页控件,但契约是分页的,没有它 50 条之后的流水会静默看不见。
 * ⑤ 页头那句说明与页脚那句口径照画板逐字;页头右侧的「刷新」按画板做成描边幽灵键。
 */
import { computed, h, onMounted, ref } from 'vue'
import { NDrawer, NDrawerContent, useMessage, type DataTableColumns } from 'naive-ui'
import {
  DataTable,
  ErrorBanner,
  FieldGrid,
  NavIcon,
  PageHeader,
  PanelCard,
  StatTile,
  StatusTag,
  settlementStatusGroup,
  settlementStatusLabel,
  type ScopeNote,
} from '@sea-drone/ui'
import { formatCentsYuan, formatRateBp } from '@sea-drone/shared'
import { api, ApiError, type Settlement } from '../api/client'

/** 这一页是运营端的第二块深色页,底座由 S-41 的第三档 tone 给(AppShell 上已挂同一档) */
const TONE = 'ops-dark' as const

const PAGE_SIZE = 50

const message = useMessage()

const rows = ref<Settlement[]>([])
/** 返回体里的 total —— 当前条件下的**全量**流水条数(服务端计数,不是本页行数) */
const total = ref(0)
const page = ref(1)
const loading = ref(false)
/** 抽屉里那一笔的明细;null = 抽屉关着(与 O1 同一套开合写法) */
const selected = ref<Settlement | null>(null)
const lastError = ref<string | null>(null)

/**
 * 第一张统计卡的标题走单点的结算状态表:它是 payable 的中文名,而这一格
 * 与那个取值恰好同名(统计卡数的就是这些流水)。**不能在页面里手写这三个字** ——
 * 中文名只在 packages/ui/src/labels.ts 定义一次,视图里写一份字面量
 * check-enum-labels ③ 会当场报红(同「在途」与「运输中」那一处的纪律)。
 */
const payableLabel = settlementStatusLabel('payable')

const NOTES: readonly ScopeNote[] = [
  {
    title: '这一页能做什么',
    body:
      '看全部结算流水 —— 一单一条,订单完成那一刻建出来(L0-04 的两个科目:应付承运商与平台佣金,' +
      '代办服务费全额归平台)。点开任一行的单号,看这笔账的九个数与三条等式。' +
      '取消退款过的单会多一条冲正流水,它那几个数是冲正后的数。',
    refs: ['S-09', 'L0-04', 'M-10', 'M-11', 'M-13'],
  },
  {
    title: '三张统计卡为什么是「—」',
    body:
      '契约的列表接口只有 page / pageSize:返回体是逐条的金额,没有按状态的计数、没有求和,' +
      '而结算流水里也没有任何时间字段 —— 「本月」这个口径今天根本算不出来。' +
      '三张卡因此画破折号,各自登记了一条 @unimplemented 标记(见本页源码)。' +
      '**不许数本页条数**:把一页 50 条里数出来的数说成全部,页面上完全看不出来。',
    refs: ['S-43'],
  },
  {
    title: '状态四个取值里,本期只会有两个',
    body:
      '结算流水有四个取值:应付承运商(订单完成时写下的就是它)、冲正(取消退款链路写下的),' +
      '以及平台已打款与两段式中间态 —— 后两个本期没有任何路径产出:打款走线下对公转账,' +
      '而结算是单段式的,不建「付款即建流水、完成再定金额」那种中间态(Y-19)。' +
      '画板上那三行「已结算」因此今天一条也产不出来;「结算失败」在契约里没有这一档 ——' +
      '冲正是「客户取消了、钱退回去了」,不是「系统出错了」。两个词都登记在 design-copy 的豁免里。',
    refs: ['Y-19', 'M-12'],
  },
  {
    title: '承运商与成交时间两列',
    body:
      'Settlement 返回体里没有这两样:既没有承运商(只有一个单号),也没有任何时间字段。' +
      '按「拿不到就画破折号」处理,不拿订单接口逐行顶替 —— 那是每页 50 次请求,' +
      '而且订单上的「下单时间」与画板要的「成交时间」是两回事。',
    refs: ['S-43'],
  },
  {
    title: '列表有多少条、怎么看更早的',
    body:
      '表格按契约分页(每页 50 条),页码在表格右下角。契约里没有筛选参数,' +
      '所以看不到的单要翻页,不能靠筛。',
    refs: [],
  },
]

/** 抽屉里那张卡的「这九个数是干嘛的」—— 挂在它说的那件事旁边 */
const DETAIL_NOTES: readonly ScopeNote[] = [
  {
    title: '这九个数是给对账用的',
    body:
      '这一页能核的三条等式都在这几个数上:实付 = 应付承运商 + 平台佣金 + 代办费;' +
      '佣金基数 = 运费(代办费不进基数);平台佣金 = 向下取整(佣金基数 × 佣金率),尾差归平台。' +
      '它们由 probes/invariants.mjs 每次跑一遍 —— 这几个数不是为了好看,它们就是对账要核的等式。',
    refs: ['L0-04', 'M-10', 'M-11', 'M-13', 'M-14'],
  },
  {
    title: '空跑费与退款额什么时候有值',
    body:
      '只有取消退款过的单才有这两项(S-08 的三段退款):冲正之后「退款额 + 空跑费 = 实付」,' +
      '而且应付承运商 = 空跑费。没发生过退款的单,这两格是破折号 —— 那是「还没有」,不是「是 0」。',
    refs: ['M-12', 'Y-19'],
  },
]

// ── 三张统计卡各自的缺口(画「—」的三个理由各不相同)────────────────
//
// @unimplemented(reason="「待结算」要按状态统计与求和,而契约 opsListSettlements 只有 page/pageSize,返回体是逐条流水:没有按状态的过滤、没有计数、没有聚合端点。不许数本页条数,也不许拿「全部流水的 total」顶替 —— 退款过的单会多一条冲正流水,那个口径静默偏大,而页面上完全看不出来", owner="me", blocks="O2 统计卡「待结算」")
//
// @unimplemented(reason="「本月已结」要的是平台已打款那批按月算数与金额:打款状态本期没有任何路径产出(线下对公转账,Y-19),而 Settlement 里没有任何时间字段 —— 连「本月」这个口径都无从算起。要它先给契约加一个时间字段,再加一个按状态与月份的聚合端点", owner="me", blocks="O2 统计卡「本月已结」")
//
// @unimplemented(reason="「平台佣金」要的是全部流水 commissionAmount 的求和,而契约的列表接口只回逐条金额,没有求和端点。前端把每一页都拉下来再累加是另一条路,但那等于在页面里造一个与库脱钩的口径(列表一分页、它一漏页就静默偏小)—— 不拿它冒充", owner="me", blocks="O2 统计卡「平台佣金」")

/**
 * 九列,逐列一的数据源(画板 O2 的表头逐字):
 *   单号 orderNo(点它打开抽屉)· 结算状态 status(StatusTag,中文与配色走单点的
 *   结算状态表)· 承运商 **无字段** → 破折号 · 运费 freightFee · 代办费 agencyFee ·
 *   佣金基数 commissionBase(契约保证恒等于运费,单独给字段就是为了让它可断言)·
 *   佣金 commissionAmount · 应付承运商 carrierPayable · 成交时间 **无字段** → 破折号。
 * 金额一律 ¥ + 整数分转字符串(M-14);五列金额右对齐 + tabular-nums。
 */
const columns = computed<DataTableColumns<Settlement>>(() => [
  {
    title: '单号',
    key: 'orderNo',
    width: 170,
    render: (row) =>
      h(
        'button',
        { type: 'button', class: 'order-no', onClick: () => void openDetail(row.orderNo) },
        row.orderNo,
      ),
  },
  {
    title: '结算状态',
    key: 'status',
    width: 100,
    render: (row) =>
      h(StatusTag, {
        status: row.status,
        label: settlementStatusLabel(row.status),
        group: settlementStatusGroup(row.status),
        tone: TONE,
        size: 'sm',
      }),
  },
  // 承运商:契约里没有这个字段 —— 破折号,理由见下面那条 @unimplemented
  // @unimplemented(reason="Settlement schema 里没有承运商字段(既没有 carrierId 也没有名字),这一列无处可来。逐行去订单接口捞一次是每页 50 次请求的代用口径,不是这个返回体里的数 —— 按「拿不到就画破折号」处理。要它先给 Settlement 加承运商字段", owner="me", blocks="O2 表格「承运商」列")
  { title: '承运商', key: 'carrier', width: 120, render: () => '—' },
  { title: '运费', key: 'freightFee', width: 110, className: 'num', render: (row) => yuan(row.freightFee) },
  { title: '代办费', key: 'agencyFee', width: 110, className: 'num', render: (row) => yuan(row.agencyFee) },
  {
    title: '佣金基数',
    key: 'commissionBase',
    width: 110,
    className: 'num',
    render: (row) => yuan(row.commissionBase),
  },
  { title: '佣金', key: 'commissionAmount', width: 100, className: 'num', render: (row) => yuan(row.commissionAmount) },
  {
    title: '应付承运商',
    key: 'carrierPayable',
    width: 120,
    className: 'num',
    render: (row) => yuan(row.carrierPayable),
  },
  // 成交时间:契约里没有任何时间字段 —— 破折号,理由见下面那条 @unimplemented
  // @unimplemented(reason="Settlement schema 里没有任何时间字段(成交时间、结算时间都没有),画板这一列无处可来。订单上的 createdAt 是「下单时间」不是「成交时间」,拿它顶替是一句看不出来的假话 —— 按「拿不到就画破折号」处理。要它先给 Settlement 加时间字段", owner="me", blocks="O2 表格「成交时间」列")
  { title: '成交时间', key: 'tradedAt', width: 120, render: () => '—' },
])

/** 金额展示:整数分 → 「¥1,280.00」,只用于展示(M-14) */
function yuan(cents: number): string {
  return `¥${formatCentsYuan(cents)}`
}

/**
 * 抽屉的九行(画板没画详情,这九行就是契约 Settlement 里那九件套 ——
 * 「五条钱的等式读的就是这个返回体」,详情要让人看得见那九个数)。
 * 空跑费与退款额没值时为 null,FieldGrid 画破折号;微信交易号是回调幂等键的一半
 * (M-15),对账时要拿它去核微信那一侧。
 */
const detailFields = computed(() => {
  const s = selected.value
  if (!s) return []
  return [
    { label: '客户实付', value: yuan(s.paidAmount) },
    { label: '运费', value: yuan(s.freightFee) },
    { label: '代办费', value: yuan(s.agencyFee) },
    { label: '佣金基数', value: yuan(s.commissionBase) },
    { label: '佣金率', value: formatRateBp(s.commissionRateBp) },
    { label: '平台佣金', value: yuan(s.commissionAmount) },
    { label: '应付承运商', value: yuan(s.carrierPayable) },
    { label: '空跑费', value: s.deadRunFee === null ? null : yuan(s.deadRunFee) },
    { label: '退款额', value: s.refundAmount === null ? null : yuan(s.refundAmount) },
    { label: '微信交易号', value: s.transactionId, mono: true as const },
  ]
})

/** 抽屉的开合挂在选中的那一笔上 —— 关掉抽屉就是「不再看这一笔」,不额外多一个状态 */
const drawerOpen = computed({
  get: () => selected.value !== null,
  set: (open: boolean) => {
    if (open) return
    selected.value = null
  },
})

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function refresh(): Promise<void> {
  loading.value = true
  try {
    const res = await api.listSettlements({ page: page.value, pageSize: PAGE_SIZE })
    rows.value = res.items
    total.value = res.total
    lastError.value = null
  } catch (e) {
    show(e)
  } finally {
    loading.value = false
  }
}

/** 翻页:服务端分页(契约的 page/pageSize),不在本地切片 —— total 是服务端的数 */
function goPage(next: number): void {
  page.value = next
  void refresh()
}

async function openDetail(orderNo: string): Promise<void> {
  try {
    selected.value = await api.getSettlement(orderNo)
  } catch (e) {
    selected.value = null
    show(e)
  }
}

onMounted(() => {
  void refresh()
})
</script>

<template>
  <div class="page">
    <PageHeader :tone="TONE" title="结算与对账" :notes="NOTES">
      <template #actions>
        <!-- 画板页头右端那一行:先说明、再刷新(说明照画板逐字) -->
        <span class="header-note">按订单成交时的快照对账,不随计价表改版漂移</span>
        <button type="button" class="ghost" :disabled="loading" @click="refresh">
          <NavIcon name="refresh" :size="14" />
          刷新
        </button>
      </template>
    </PageHeader>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="上一次操作失败" />

    <!--
      三张统计卡:契约里没有聚合端点(也没有状态过滤、没有时间字段),
      三张都画「—」—— 理由见源码里那三条 @unimplemented 标记。
      色条按画板:待结算 = 琥珀、本月已结 = 品牌蓝、平台佣金 = 绿
    -->
    <div class="stats">
      <StatTile theme="ops-dark" bar="right" :watermark="false" :label="payableLabel" value="—" tone="warning" />
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="本月已结" value="—" tone="brand" />
      <StatTile theme="ops-dark" bar="right" :watermark="false" label="平台佣金" value="—" tone="success" />
    </div>

    <PanelCard :tone="TONE" padding="md">
      <DataTable
        :tone="TONE"
        :columns="columns"
        :data="rows"
        :row-key="(row: Settlement) => row.orderNo"
        :loading="loading"
        :pagination="{ page, pageSize: PAGE_SIZE, itemCount: total, onPage: goPage }"
        empty-text="还没有结算流水 —— 订单完成那一刻这里会出现它的对账记录"
      />
    </PanelCard>

    <!-- 页脚说明(画板逐字,整宽一行) -->
    <p class="foot-note">佣金基数只取运费,不含代办服务费;退款按成交时的空跑费规则重算,不回写已结单。</p>

    <n-drawer v-model:show="drawerOpen" :width="760" placement="right">
      <n-drawer-content v-if="selected" :native-scrollbar="false" closable>
        <template #header>
          <div class="drawer-head">
            <span class="drawer-title">结算明细</span>
            <span class="drawer-sub">
              {{ selected.orderNo }} · {{ settlementStatusLabel(selected.status) }}
            </span>
          </div>
        </template>

        <div class="drawer-body">
          <PanelCard :tone="TONE" title="对账明细" :notes="DETAIL_NOTES">
            <FieldGrid :tone="TONE" :items="detailFields" />
          </PanelCard>
        </div>
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<style scoped>
/* 页头与各块之间的纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 页头右端那句口径说明(画板:12px 三级灰,排在刷新键左边) */
.header-note {
  font-size: 12px;
  color: var(--sd-text3);
}

/* 刷新:画板的幽灵键(无底、1px 描边 border-strong、内边距 [8,14]、圆角 sm) */
.ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--sd-border-strong);
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost:hover:not(:disabled) {
  color: var(--sd-text1);
  border-color: var(--sd-brand);
}

.ghost:disabled {
  opacity: 0.6;
  cursor: default;
}

.stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.foot-note {
  margin: 0;
  font-size: 12px;
  color: var(--sd-text3);
}

/* ── 抽屉 ───────────────────────────────────────────── */

.drawer-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.drawer-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--sd-text1);
}

.drawer-sub {
  font-size: 13px;
  color: var(--sd-text3);
}

.drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 表格单元格是 DataTable 渲染出来的,拿不到本组件的 scope 属性 —— 用 :deep 穿进去 */
:deep(.order-no) {
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  font-family: var(--sd-font-mono);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text1);
  cursor: pointer;
}

:deep(.order-no:hover) {
  color: var(--sd-brand);
  text-decoration: underline;
}

/* 五列金额右对齐:同一列的数按小数点对齐才扫得动 */
:deep(.num) {
  text-align: right;
}

@media (max-width: 1200px) {
  .stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .stats {
    grid-template-columns: 1fr;
  }
}
</style>
